import { useState } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect } from "react";
import { Slider } from "../ui/slider";
import { useLang } from "../../i18n/LangContext";
import { Reveal, Overline } from "./Reveal";
import { scrollToId } from "./SmoothScroll";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

// Sample rates for illustration only
const RATE = { aeps: 8, dmt: 0.004, bbps: 2, other: 60 };

const AnimatedNum = ({ value, testid }) => {
  const mv = useMotionValue(value);
  const txt = useTransform(mv, (v) => inr(v));
  useEffect(() => {
    const c = animate(mv, value, { duration: 0.6, ease: "easeOut" });
    return () => c.stop();
  }, [value, mv]);
  return <motion.span data-testid={testid}>{txt}</motion.span>;
};

const Row = ({ label, value, display, min, max, step, onChange, testid }) => (
  <div>
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-slate-300">{label}</span>
      <span className="font-mono text-lg font-semibold text-white">{display}</span>
    </div>
    <Slider data-testid={testid} className="calc-slider mt-4" value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
  </div>
);

export const Calculator = () => {
  const { t } = useLang();
  const c = t.calc;
  const [v, setV] = useState({ aeps: 40, dmt: 50000, bbps: 25, other: 10 });
  const set = (k) => (x) => setV((p) => ({ ...p, [k]: x }));
  const monthly = (v.aeps * RATE.aeps + v.dmt * RATE.dmt + v.bbps * RATE.bbps) * 26 + v.other * RATE.other * 4;

  return (
    <section id="calculator" className="relative py-24 lg:py-32" data-testid="income-calculator">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 overflow-hidden rounded-[32px] border border-white/10 bg-card p-7 sm:p-10 lg:grid-cols-2 lg:gap-16 lg:p-14">
          <Reveal>
            <Overline>{c.over}</Overline>
            <h2 className="section-title mt-5">{c.title}</h2>
            <p className="mt-4 text-slate-300">{c.sub}</p>
            <div className="mt-10 space-y-9">
              <Row label={c.aeps} value={v.aeps} display={v.aeps} min={5} max={200} step={1} onChange={set("aeps")} testid="calc-aeps-slider" />
              <Row label={c.dmt} value={v.dmt} display={inr(v.dmt)} min={5000} max={500000} step={5000} onChange={set("dmt")} testid="calc-dmt-slider" />
              <Row label={c.bbps} value={v.bbps} display={v.bbps} min={0} max={150} step={1} onChange={set("bbps")} testid="calc-bbps-slider" />
              <Row label={c.other} value={v.other} display={v.other} min={0} max={60} step={1} onChange={set("other")} testid="calc-other-slider" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-col justify-between rounded-[24px] bg-saffron p-8 text-ink lg:p-10">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em]">{c.monthly}</p>
              <p className="mt-3 font-display text-5xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl">
                <AnimatedNum value={monthly} testid="calc-monthly-value" />
              </p>
              <div className="my-8 h-px bg-ink/20" />
              <p className="text-sm font-semibold uppercase tracking-[0.18em]">{c.yearly}</p>
              <p className="mt-2 font-display text-3xl font-bold sm:text-4xl">
                <AnimatedNum value={monthly * 12} testid="calc-yearly-value" />
              </p>
            </div>
            <div className="mt-10">
              <p className="text-xs leading-relaxed text-ink/80">{c.note}</p>
              <button onClick={() => scrollToId("join")} className="mt-5 inline-flex items-center rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5" data-testid="calc-claim-btn">
                {c.cta}
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
